import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterOutlet } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { PrimeNG } from 'primeng/config';
import { GuardEventObserver } from './core/services/guard-event-observer.service';
import { SystemService } from './shared/system.service';
import { TerminologyService } from './shared/terminology.service';
import { AppConfigService } from './core/config/app-config.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [CommonModule, RouterOutlet],
  templateUrl: './app.html',
  styleUrl: './app.scss'
})
export class App implements OnInit {
  title = 'Leanbyte';
  loading: boolean;

  private translateService = inject(TranslateService);
  private systemService = inject(SystemService);
  private terminologyService = inject(TerminologyService);
  private guardObserver = inject(GuardEventObserver);
  private appConfig = inject(AppConfigService);
  private primeng = inject(PrimeNG);

  constructor() {
    // Only show loading spinner for Microsoft login mode.
    // For LDAP, loading is always false → router-outlet is immediately visible.
    this.loading = this.appConfig.microsoftLogin;

    if (this.appConfig.microsoftLogin) {
      this.guardObserver.getLoadingState().subscribe(state => {
        this.loading = state;
      });
    }
  }

  ngOnInit(): void {
    this.translateService.setDefaultLang('de');
    const savedLang = localStorage.getItem('userLanguage');
    this.translateService.use(savedLang || 'de');

    this.applyPrimeNgTranslation();
    this.translateService.onLangChange.subscribe(() => this.applyPrimeNgTranslation());

    // Per-client audit terminology override (Area/Workplace → client term); no-op for default tenants.
    this.terminologyService.init();

    if (!this.appConfig.microsoftLogin) {
      // LDAP mode: load system config immediately (no MsalInterceptor to cause issues).
      // MSAL mode: deferred to AppMainComponent which renders after MsalGuard authenticates,
      // preventing MsalInterceptor from triggering an infinite redirect loop.
      this.systemService.load();
    }
  }

  private applyPrimeNgTranslation(): void {
    const noResults = this.translateService.instant('common.no-results');
    this.primeng.setTranslation({
      emptyMessage: noResults,
      emptyFilterMessage: noResults,
      emptySearchMessage: noResults,
      emptySelectionMessage: this.translateService.instant('common.no-selection'),
    });
  }
}
